/**
 * NCRs with no supplier — internal / production rejects,
 * imported from the ERP with an empty vendor (backend: quality.no_supplier).
 *
 * They are the customer's alone: no supplier ever sees one, they never move a
 * supplier's score, and nothing on them can be sent to, or asked of, a
 * supplier. The backend already labels them in `supplier_name` /
 * `supplier_display`; these helpers keep the frontend from reading a null
 * supplier as a real one.
 */
import type { NonConformanceReport } from '../../lib/api';

/** The label the backend sends for a supplier-less NCR. */
export const NO_SUPPLIER_LABEL = 'No supplier (internal)';

/** Supplier filter value meaning "only NCRs with no supplier" (sends no_supplier=true). */
export const NO_SUPPLIER_FILTER_VALUE = 'none';

type SupplierFields = Pick<NonConformanceReport, 'supplier'> &
  Partial<Pick<NonConformanceReport, 'supplier_name' | 'supplier_display'>>;

/** True when the NCR has a supplier to send things to. */
export function ncrHasSupplier(ncr: Pick<NonConformanceReport, 'supplier'>): boolean {
  return ncr.supplier !== null && ncr.supplier !== undefined;
}

/** The supplier name to show for an NCR — the internal label when there is none. */
export function ncrSupplierLabel(ncr: SupplierFields): string {
  if (!ncrHasSupplier(ncr)) return ncr.supplier_display || NO_SUPPLIER_LABEL;
  return ncr.supplier_name || ncr.supplier_display || '';
}
